


import { Component, Vue, Prop, Emit, Watch, Ref } from 'vue-property-decorator';
import SelectionPanel from '../SelectionPanel/index.vue';
import VerticalSelect from '../VerticalSelect/index.vue';
import GridSelect from '../GridSelect/index.vue';
import ZoneNavigation from '../ZoneNavigation/index.vue';
import SubzoneNavigation from '../SubzoneNavigation/index.vue';
import ResponsiveImage from '../utils/ResponsiveImage/index.vue';
import { SelectType } from '../SelectionPanel/SelectionPanel.models';
import { ResponsiveImageMedia } from '../utils/ResponsiveImage/ResponsiveImage.models';
import { Module, Floor, Zone, HouseConfiguration, GallerySlide, Selection } from '../../utils/api';
import { HouseView, imageMediaDimenstions, layeringEngineMediaDimenstions, RenderMode } from './ConfContainer.models';
import TransitionHeightCrossfade from '../utils/transitions/TransitionHeightCrossfade/index.vue';
import TransitionDelayedCrossfade from '../utils/transitions/TransitionDelayedCrossfade/index.vue';
import { gsap } from 'gsap';
import LayeringEngine from '../LayeringEngine/index.vue';
import { Layer } from '../LayeringEngine/LayeringEngine.models';
import generateITSMedia from '@/utils/helpers/generateITSMedia';
import getFloorLayers from '@/utils/helpers/getFloorLayers';
import getOutsideLayers from '@/utils/helpers/getOutsideLayers';
import CrossfadeResponsiveImage from '../utils/CrossfadeResponsiveImage/index.vue';
import SimpleLoader from '../utils/SimpleLoader/index.vue';
import { namespace } from 'vuex-class';
import HintTool from '../HintTool/index.vue';
import { Hint } from '../HintTool/HintTool.models';
import { resizeImageUrl } from '@/utils/helpers/resizeImageUrl';
import { FloorState } from '@/store/modules/app';
import { TranslateResult } from 'vue-i18n';

const configurationModule = namespace('configuration');
const notifyModule = namespace('notify');

export interface EnablerOptions extends FloorState {
  moduleID: string;
}

export interface EnablerConfig {
  floorName: string;
  newModuleName: string;
  currentModuleName: string;
  additionalPrice?: number;
  text: string | TranslateResult;
}

@Component({
  components: {
    ResponsiveImage,
    SelectionPanel,
    VerticalSelect,
    GridSelect,
    ZoneNavigation,
    SubzoneNavigation,
    TransitionHeightCrossfade,
    TransitionDelayedCrossfade,
    LayeringEngine,
    CrossfadeResponsiveImage,
    HintTool,
    SimpleLoader
  }
})
export default class ConfContainer extends Vue {
  @Ref('selectionPanel') private selectionPanelEl!: Vue;

  @Prop() private configuration!: HouseConfiguration;
  @Prop() private floor!: Floor;
  @Prop() private activeZone!: Zone;
  @Prop() private activeSubzone!: Zone;
  @Prop({ default: () => [] }) completedZoneIDs!: string[];
  @Prop({ default: SelectType.Vertical }) private selectType!: SelectType;
  // Which component to use
  @Prop({ default: RenderMode.Image }) private renderMode!: RenderMode;
  // Which styles and image matix to use
  @Prop({ default: RenderMode.Image }) private displayMode!: RenderMode;
  @Prop({ default: HouseView.Inside }) private houseView!: HouseView;
  @Prop({ default: false }) private showLayoutNav!: boolean;
  @Prop({ default: true }) private showImage!: boolean;
  @Prop({ default: true }) private showPanel!: boolean;
  @Prop({ default: false }) private enablerOptions!: EnablerOptions;
  @Prop({ default: false }) private disableUpdate!: boolean;
  @Prop() private resolveConflict!: boolean;
  /**
   * if true, fade out the graphics
   */
  @Prop({ default: false }) public fadeLayer!: boolean;
  @configurationModule.State private apiLoading!: boolean;
  @configurationModule.Getter private floorByIDs!: (floorID: string, zoneID?: string) => Floor | undefined;
  @configurationModule.Action private updateConfiguration!: (payload: {
    update?: Selection[];
    resolveConflicts?: boolean;
  }) => void;

  @notifyModule.Action private openGalleryModal!: (slides: GallerySlide[]) => void;

  // to make ts enums work in vue template
  private SelectType: typeof SelectType = SelectType;
  private RenderMode: typeof RenderMode = RenderMode;
  private HouseView: typeof HouseView = HouseView;

  private loading = false;

  @Emit()
  private selectOption(option: Module) {
    if (!this.disableUpdate) {
      this.initiateConfigurationUpdate(option);
    }

    return option;
  }

  @Emit()
  private changeZone(zoneID: string) {
    return zoneID;
  }

  @Emit()
  private showLayout() {
    return;
  }

  @Watch('showPanel')
  private showPanelChanged(value: boolean) {
    if (value) {
      gsap.fromTo(
        this.selectionPanelEl.$el,
        { transform: 'translateX(30px)', opacity: 0 },
        { transform: 'translateX(0)', opacity: 1, duration: 2, delay: 2.4 }
      );
    }
  }

  private get showZoneNavigation(): boolean {
    return this.floor.zones.length > 1 || this.showLayoutNav;
  }

  private get selectedOption(): Module | undefined {
    return this.selectedOptions?.find(option => option.isSelected);
  }

  private get subzones(): Zone[] {
    return this.activeZone?.zones ?? [];
  }

  private get isDeactivated(): boolean {
    return this.activeZone?.isDeactivated;
  }

  private get isEnabler(): boolean {
    return this.isDeactivated && !!this.enablerConfig;
  }

  private get enablerConfig(): EnablerConfig | null {
    const floor = this.floorByIDs(this.enablerOptions.floorID, this.enablerOptions.zoneID);
    const floorModules = floor?.zones[0].modules;

    const currentModule = floorModules?.find((module: Module) => module.isSelected);
    const newModule = floorModules?.find((module: Module) => module.ID === this.enablerOptions.moduleID);

    if (!floor || !currentModule || !newModule) {
      return null;
    }

    const floorName = floor.name;
    const isEnglish = this.$i18n.locale === 'en-GB';
    const activeZoneName = this.activeZone.name;

    const translateKey =
      this.enablerOptions.zoneID === 'ROZ'
        ? 'views.summary.floorsOverview.attic.enablerText'
        : 'global.fields.enabler.hint_text';

    const text = this.$t(translateKey, {
      currentOption: currentModule.name,
      blockingStep: isEnglish ? floorName.toLowerCase() : floorName,
      possibleOption: newModule.name,
      currentStep: isEnglish ? activeZoneName.toLowerCase() : activeZoneName
    });

    return {
      text,
      floorName,
      currentModuleName: currentModule.name,
      newModuleName: newModule.name,
      additionalPrice: newModule.price
    };
  }

  @Emit()
  private changeSubzone(subzoneID: string) {
    return subzoneID;
  }

  private get selectedOptions(): Module[] {
    let selectedOptions: Module[] | undefined;

    if (this.activeSubzone) {
      selectedOptions = this.activeSubzone.modules;
    } else {
      selectedOptions = this.activeZone ? this.activeZone.modules : this.floor.zones[0].modules;
    }

    return selectedOptions || [];
  }

  private get image(): ResponsiveImageMedia | undefined {
    let image = '';

    if (this.selectedOption) {
      image = this.selectedOption.image;
    } else if (this.selectedOptions.length) {
      image = this.selectedOptions[0].image;
    }

    return image
      ? generateITSMedia(
          image,
          this.displayMode === RenderMode.Image ? imageMediaDimenstions : layeringEngineMediaDimenstions
        )
      : undefined;
  }

  private get layers(): Layer[] {
    return this.houseView === HouseView.Inside
      ? getFloorLayers(this.floor, layeringEngineMediaDimenstions)
      : getOutsideLayers(this.configuration, layeringEngineMediaDimenstions);
  }

  private get baseLayer(): Layer | undefined {
    return this.floor.image && this.houseView === HouseView.Inside
      ? {
          ID: this.floor.ID,
          alt: this.floor.name,
          media: generateITSMedia(this.floor.image, layeringEngineMediaDimenstions)
        }
      : undefined;
  }

  public get hintForCurrentModule(): Hint | null {
    if (!this.selectedOption || !this.selectedOption.gallery) {
      return null;
    }

    const { headline, text, images } = this.selectedOption.gallery.slides[0];
    if (!headline || !text) {
      return null;
    }

    // extract an image with target dimensions, if given
    const imageUrl = images[1] ? resizeImageUrl(images[1].image, [127, 74]) : undefined;

    if (this.selectedOption && !this.hideHint) {
      return {
        headline,
        text,
        imageUrl
      };
    }
    return null;
  }

  public get slidesForCurrentModule(): GallerySlide[] {
    if (!this.selectedOption || !this.selectedOption.gallery) {
      return [];
    }

    return this.selectedOption.gallery.slides;
  }

  private initiateConfigurationUpdate(module: Module, additionalUpdates?: Selection[]) {
    let update: Selection[] = [
      {
        zoneID: this.activeSubzone?.ID || this.activeZone?.ID || this.floor?.zones[0].ID,
        moduleID: module.ID,
        isSelected: true,
        imageConfiguration: ''
      }
    ];

    if (additionalUpdates) {
      update = update.concat(additionalUpdates);
    }

    const payload: {
      update: Selection[];
      resolveConflict?: boolean;
    } = {
      update
    };

    if (this.resolveConflict) {
      payload.resolveConflict = this.resolveConflict;
    }

    this.updateConfiguration(payload);
  }

  private enableSelection() {
    const zoneID =
      this.enablerOptions.zoneID ||
      this.floorByIDs(this.enablerOptions.floorID, this.enablerOptions.zoneID)?.zones[0].ID;
    if (!zoneID) {
      return;
    }

    const defaultModule = this.selectedOptions.find((module: Module) => module.price === 0) || this.selectedOptions[0];

    this.initiateConfigurationUpdate(defaultModule, [
      {
        zoneID,
        moduleID: this.enablerOptions.moduleID,
        isSelected: true,
        imageConfiguration: ''
      }
    ]);
  }

  public hintToolClicked() {
    this.openGalleryModal(this.slidesForCurrentModule);
  }

  public get hintKey(): string {
    return this.floor.ID + '-' + (this.selectedOption ? this.selectedOption.ID : '');
  }

  private hideHint = true;

  protected mounted() {
    // after mounting, let some time pass before fading in the hint tool
    setTimeout(() => (this.hideHint = false), 1000);
  }

  protected beforeDestroy() {
    // before being destroyed, manually trigger the leave transition (Vue won't, for some reason)
    if (this.$refs.hinttool) {
      (this.$refs.hinttool as Element).classList.add('delayed-crossfade-leave-to');
      (this.$refs.hinttool as Element).classList.add('delayed-crossfade-leave-active');
    }
  }
}
